import { autorun, makeObservable, observable, runInAction } from 'mobx';
import { SHELL_ALL_WORK_AREAS_SCOPE } from '@featit/shared/shell-paths';
import { PHONE_BADGE_COUNTS, type PhoneBadgeCountsType } from '@featit/shared/types/index';
import { decisionApi } from '@/services/DecisionApi';
import { onShellEvent, postShellMessage } from './shellBridge';

/**
 * F-0530 (AC-19.8, the captain's ruling for Jay): the Decisions badge follows the app's scope, and
 * the work-area line says what waits elsewhere, so nothing is hidden. Both are counted here, from
 * one set — every decision waiting for the person, in every work area — by one rule.
 */

/** A waiting decision, as far as counting needs it. */
export interface IWaitingDecision {
  readonly item: { readonly workAreaId?: string | null };
}

/**
 * The badge's number: the scope's own waiting decisions, or every work area's under All or when
 * the person chose "All work areas" (`badgeCounts`). `null` scope (nothing chosen yet) counts all.
 */
export function badgeCountOf(
  everywhere: readonly IWaitingDecision[],
  scope: string | null,
  mode: PhoneBadgeCountsType,
): number {
  if (
    mode === PHONE_BADGE_COUNTS.ALL_WORK_AREAS ||
    !scope ||
    scope === SHELL_ALL_WORK_AREAS_SCOPE
  ) {
    return everywhere.length;
  }
  return everywhere.filter((decision) => decision.item.workAreaId === scope).length;
}

/** What waits outside one work area; nothing is "elsewhere" under All. */
export function waitingElsewhereOf(
  everywhere: readonly IWaitingDecision[],
  workAreaId: string | null,
): number {
  if (!workAreaId || workAreaId === SHELL_ALL_WORK_AREAS_SCOPE) return 0;
  return everywhere.filter((decision) => decision.item.workAreaId !== workAreaId).length;
}

/** "2 waiting in other work areas", or `null` for none. */
export function waitingElsewhereLabel(count: number): string | null {
  return count > 0 ? `${count} waiting in other work areas` : null;
}

/**
 * This web view's copy of every waiting decision. Read when the app starts, when its own decisions
 * change (an answer, a take-back: `refreshOn`), when the app comes back, and when the work areas
 * changed. `undefined` until the first answer.
 */
export class WaitingEverywhere {
  decisions: readonly IWaitingDecision[] | undefined = undefined;
  private sequence = 0;

  constructor(
    private readonly read: () => Promise<readonly IWaitingDecision[]> = () =>
      decisionApi.waitingForMeEverywhere(),
  ) {
    makeObservable<WaitingEverywhere, never>(this, { decisions: observable.ref });
  }

  async refresh(): Promise<void> {
    const sequence = ++this.sequence;
    try {
      const decisions = await this.read();
      if (sequence !== this.sequence) return;
      runInAction(() => {
        this.decisions = decisions;
      });
    } catch {
      // Kept as it was: a failed read is not "nothing waits". The next trigger reads again.
    }
  }

  /** Starts following; returns the disposer. `changed` is read in a reaction to know when to read. */
  follow(changed: () => unknown): () => void {
    void this.refresh();
    let first = true;
    const stopReaction = autorun(() => {
      changed();
      if (first) {
        first = false;
        return;
      }
      void this.refresh();
    });
    const stopEvents = onShellEvent((event) => {
      if (event.type === 'foreground' || event.type === 'workAreasChanged') void this.refresh();
    });
    return () => {
      stopReaction();
      stopEvents();
    };
  }
}

/** The one copy for this web view (each tab is a web view of its own). */
export const waitingEverywhere = new WaitingEverywhere();

/**
 * AC-19.8: the app's badge, counted for the scope by the setting. Every web view posts the same
 * number for the same scope and setting, so it does not matter which one posted last.
 */
export function followScopeBadge(
  source: Pick<WaitingEverywhere, 'decisions'>,
  scope: () => string | null,
  mode: () => PhoneBadgeCountsType,
): () => void {
  return autorun(() => {
    const decisions = source.decisions;
    if (!decisions) return;
    postShellMessage({ type: 'badge', decisionsWaiting: badgeCountOf(decisions, scope(), mode()) });
  });
}
